import React from "react";
import { createPortal } from "react-dom";
import { Bookmark } from "lucide-react";
import {
  MODAL_ANIMATION_CSS,
  MODAL_OVERLAY_STYLE,
  MODAL_PANEL_STYLE,
  useBodyScrollLock,
} from "./modalEffects";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";

interface ModalConfirmProps {
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  isConfirming?: boolean;
  variant?: "default" | "bookmark";
  eyebrow?: string;
  heading?: string;
  icon?: React.ReactNode;
}

const ModalConfirm: React.FC<ModalConfirmProps> = ({
  message,
  onCancel,
  onConfirm,
  cancelLabel = "Cofnij",
  confirmLabel = "Anuluj zamówienie",
  isConfirming = false,
  variant = "default",
  eyebrow,
  heading,
  icon,
}) => {
  useBodyScrollLock(true);

  if (variant === "bookmark") {
    const content = (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm"
        style={MODAL_OVERLAY_STYLE}
      >
        <style>{MODAL_ANIMATION_CSS}</style>
        <div
          className="w-full max-w-lg overflow-hidden rounded-[30px] border border-white/35 bg-[linear-gradient(180deg,rgba(255,252,248,0.98),rgba(248,250,252,0.98))] shadow-[0_40px_120px_rgba(15,23,42,0.24)]"
          style={MODAL_PANEL_STYLE}
        >
          <div className="border-b border-slate-200/80 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,248,240,0.9))] px-5 py-6 sm:px-7">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] border border-white/60 bg-white/80 text-[#f6851f] shadow-[0_18px_40px_rgba(246,133,31,0.16)]">
                {icon ?? (
                  <Bookmark size={24} strokeWidth={2.1} aria-hidden="true" />
                )}
              </div>

              <div>
                {eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">
                    {eyebrow}
                  </p>
                ) : null}
                {heading ? (
                  <h2 className="mt-1 text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                    {heading}
                  </h2>
                ) : null}
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {message}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-around p-5 sm:p-7">
            <button
              onClick={onCancel}
              disabled={isConfirming}
              className={`px-6 py-3 rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
              style={getActionButtonStyle("slate")}
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={isConfirming}
              className={`px-6 py-3 rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
              style={getActionButtonStyle("red")}
            >
              {isConfirming ? "Trwa..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );

    return typeof document !== "undefined"
      ? createPortal(content, document.body)
      : content;
  }

  const content = (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="bg-white p-10 rounded-lg shadow-lg w-fit">
        <h3 className="text-md font-regular text-center">{message}</h3>
        <div className="flex justify-around mt-6">
          <button
            onClick={onCancel}
            disabled={isConfirming}
            className="bg-gray-500 text-white text-sm px-6 py-2 font-bold rounded-full hover:bg-gray-400 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirming}
            className="bg-red-500 text-white text-sm px-6 py-2 font-bold rounded-full hover:bg-red-400 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isConfirming ? "Trwa..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(content, document.body)
    : content;
};

export default ModalConfirm;
